import React from 'react';
import { MouseEventHandler, ReactNode } from 'react';
import { BeatLoader } from 'react-spinners';
import styled, { useTheme } from 'styled-components';

interface ButtonParameters {
  children: string | ReactNode;
  onClick?: MouseEventHandler;
  loading?: boolean;
  disabled?: boolean;
  secondary?: boolean;
  ternary?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset' | undefined;
  style?: React.CSSProperties;
}

interface StyledButtonParameters {
  secondary?: boolean;
  ternary?: boolean;
  fullWidth?: boolean;
}

const StyledButton = styled.button<StyledButtonParameters>`
  color: ${({ secondary, ternary, theme }) =>
    secondary
      ? `${theme.text.title}`
      : ternary
        ? `${theme.background.primary}`
        : `${theme.text.primary}`};
  font-family: Manrope;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 1px;
  display: inline-flex;
  padding: 19px 28px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  border-radius: 0px;
  ${({ fullWidth }) =>
    fullWidth &&
    `
    width: 100%;
  `}
  background: ${({ secondary, ternary, theme }) =>
    secondary
      ? `${theme.background.primary}`
      : ternary
        ? 'transparent'
        : `${theme.status.error.primary}`};
  border: ${({ secondary, ternary, theme }) =>
    secondary
      ? `1px solid ${theme.text.title}`
      : ternary
        ? `1px solid ${theme.background.primary}`
        : '0px'};
  ${({ disabled, secondary, ternary, theme }) =>
    disabled
      ? `
      opacity: 0.699999988079071;
      cursor: auto;
  `
      : `
    cursor: pointer;
    @media (hover: hover) {
      &:hover{
        background: ${secondary ? 'rgba(25, 25, 25, 0.1)' : ternary ? `${theme.background.light}` : `${theme.status.error.primary}`};
      }
    }
    &:active{
      background: ${secondary ? 'rgba(25, 25, 25, 0.3)' : ternary ? `${theme.border.secondary}` : `${theme.status.error.primary}`};
    }
    &:focus{
      ${
        secondary
          ? `
        border: 1px solid rgba(25, 25, 25, 0.6);
      `
          : ternary
            ? `
        border: 1px solid ${theme.text.secondary};
      `
            : `
        border: 1px solid ${theme.status.error.primary};
        // Reduce 1px of padding to avoid changing size
        padding: 18px 27px;
      `
      }
    }
  `}
`;

const BetsButton = ({
  children,
  onClick = () => {},
  loading = false,
  disabled = false,
  secondary = false,
  ternary = false,
  type = 'button',
  fullWidth = false,
  style = undefined,
}: ButtonParameters) => {
  const theme = useTheme();

  return (
    <StyledButton
      fullWidth={fullWidth}
      type={type}
      disabled={disabled || loading}
      onClick={disabled ? () => {} : onClick}
      secondary={secondary}
      ternary={ternary}
      style={style}
    >
      {loading ? (
        <p>
          <BeatLoader
            color={
              secondary ? `${theme.text.title}` : `${theme.background.light}`
            }
            size={5}
          />
        </p>
      ) : (
        <>{children}</>
      )}
    </StyledButton>
  );
};

export default BetsButton;
